import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Mail, TrendingUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const EmailCampaignROICalculator = () => {
  const [campaignCost, setCampaignCost] = useState(1000);
  const [revenue, setRevenue] = useState(5000);
  const [roi, setRoi] = useState(0);

  useEffect(() => {
    calculateROI();
  }, [campaignCost, revenue]);

  const calculateROI = () => {
    const profit = revenue - campaignCost;
    const calculatedRoi = (profit / campaignCost) * 100;
    setRoi(parseFloat(calculatedRoi.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/email-campaign-roi-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Email Campaign ROI Calculator: Measure Marketing Returns | Free Tool</title>
        <meta name="description" content="Calculate your Email Campaign ROI instantly. Compare with industry benchmarks, get optimization strategies, and improve your email marketing returns." />
        <meta name="keywords" content="email ROI calculator, email marketing ROI, campaign performance metrics, marketing returns, email campaign analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Email Campaign ROI Calculator: Maximize Marketing Returns" />
        <meta property="og:description" content="Calculate your Email Campaign ROI instantly. Get actionable insights to improve your email marketing returns and performance." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Email Campaign ROI Calculator: Maximize Marketing Returns" />
        <meta property="twitter:description" content="Calculate your Email Campaign ROI instantly. Get actionable insights to improve your email marketing returns and performance." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Email Campaign ROI Calculator",
            "description": "Free calculator to measure and optimize email marketing campaign returns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Email ROI",
              "Industry Benchmarks",
              "ROI Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Email Campaign ROI",
              "description": "The return on investment from email marketing campaigns, measuring campaign costs against generated revenue."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Email Marketers, Digital Marketers, Business Owners"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Email Campaign ROI",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter campaign cost"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input revenue generated"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your ROI and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Email Campaign ROI Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Campaign Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={campaignCost}
                  onChange={(e) => setCampaignCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Revenue Generated</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {roi}%
            <TrendingUp className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Email Campaign ROI</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Email Campaign ROI is {roi}%. This means that for every dollar spent on the campaign, you generated ${(roi / 100 + 1).toFixed(2)} in return.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 3500-4200%</li>
          <li>B2B Services: 2800-3500%</li>
          <li>Retail: 3000-3800%</li>
          <li>Technology: 3200-4000%</li>
          <li>Media/Publishing: 2500-3200%</li>
          <li>Financial Services: 2700-3400%</li>
          <li>Healthcare: 2400-3100%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">ROI Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Segment your email list</li>
          <li>Personalize content and offers</li>
          <li>Optimize subject lines</li>
          <li>Test email designs</li>
          <li>Improve CTAs</li>
          <li>Use automation</li>
          <li>Implement lead nurturing</li>
          <li>Clean email lists regularly</li>
          <li>Monitor engagement metrics</li>
          <li>A/B test campaigns</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Cost Reduction Tips</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize email frequency</li>
          <li>Remove inactive subscribers</li>
          <li>Use email automation</li>
          <li>Improve targeting</li>
          <li>Reduce design complexity</li>
          <li>Leverage templates</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Performance Metrics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Segmentation increases ROI by 760%</li>
          <li>Personalization boosts ROI by 500%</li>
          <li>Automation improves ROI by 320%</li>
          <li>A/B testing lifts ROI by 180%</li>
          <li>List cleaning enhances ROI by 110%</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailCampaignROICalculator;
